// Iter-f6 — Brand Staff Utilisation · Branch detail (5 charts).
//
// Opened from a branch card on /staff/brand/staff-utilisation.
// Powered entirely by the server aggregate at
// `GET /reports/brand/staff-utilisation/detail/{bid}`. Period A vs
// Period B custom ranges arrive as query params so the page is
// deep-linkable and refresh-safe.
//
// No engine files are imported or edited.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Download } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Legend, LineChart, Line, LabelList, ReferenceLine,
} from "recharts";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { useDashboardSnapshot, buildRangeSnapshotFilename } from "@/lib/NotificationEngine";
import { InfoHint } from "@/components/common/InfoHint";
import { STAFF_UTILISATION_TIPS } from "@/lib/reportTips";

const SERIES_COLORS = ["#0284c7", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#be185d", "#64748b"];
const COLOR_A = "#0284c7";
const COLOR_B = "#94a3b8";

export default function BrandStaffUtilisationDetail() {
  const { bid } = useParams();
  const nav = useNavigate();
  const { staff } = useAuth();
  const [sp] = useSearchParams();
  const today = todayBali();

  const [aFrom, setAFrom] = useState(sp.get("a_from") || today);
  const [aTo,   setATo]   = useState(sp.get("a_to")   || today);
  const [bFrom, setBFrom] = useState(sp.get("b_from") || today);
  const [bTo,   setBTo]   = useState(sp.get("b_to")   || today);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const [tick, setTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setTick(keys.has("staff_utilisation"));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const canSee = isOwner(staff) || tick;

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSee || !bid) return;
    setLoading(true);
    staffApi.get(`/reports/brand/staff-utilisation/detail/${bid}`, { params })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canSee, bid, params]);
  useEffect(() => { load(); }, [load]);

  const branchLabel = data?.branch_name || "Branch";
  const { downloadRef, downloadPng, capturing } = useDashboardSnapshot({
    label: `Staff Utilisation ${branchLabel}`,
    dateFrom: aFrom,
    dateTo: aTo,
    filename: buildRangeSnapshotFilename(
      `Staff Utilisation ${branchLabel}`, aFrom, aTo, bFrom, bTo,
    ),
  });

  return (
    <StaffShell context="brand">
      <div className="flex items-center gap-2 mb-4" data-html2canvas-ignore="true">
        <Button variant="ghost" size="sm" onClick={() => nav("/staff/brand/staff-utilisation")} data-testid="brand-util-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <div ref={downloadRef} className="bg-background">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="brand-util-detail-title">
            {data?.branch_name || "…"}
          </h1>
          <p className="text-xs font-medium text-sky-600 mt-1">
            Period A · {aFrom} → {aTo} &nbsp;vs&nbsp; Period B · {bFrom} → {bTo}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadPng}
          disabled={capturing || loading}
          className="gap-1 h-9 shrink-0"
          data-testid="brand-util-detail-download"
          data-html2canvas-ignore="true"
        >
          <Download className={`w-4 h-4 ${capturing ? "animate-pulse" : ""}`} />
          {capturing ? "Capturing…" : "Download"}
        </Button>
      </div>

      {!canSee && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view the Staff Utilisation dashboard.
          </CardContent>
        </Card>
      )}

      {canSee && (
        <>
          <Card className="rounded-lg mb-4">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap" data-testid="brand-util-detail-filters">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-util-detail-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-util-detail-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-util-detail-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <SectionHeading>Section 1 · Utilisation Overview</SectionHeading>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ChartCard title="Staff Utilisation % (A vs B)" testId="brand-util-chart-util" height={420} tooltip={STAFF_UTILISATION_TIPS.utilisation}>
              <StaffUtilBar rows={data?.util_rows || []} loading={loading} />
            </ChartCard>
            <ChartCard title="Weekly Utilisation Trend · top 5 (A)" testId="brand-util-chart-trend" tooltip={STAFF_UTILISATION_TIPS.utilisation}>
              <TrendLines rows={data?.trend_rows || []} series={data?.trend_series || []} loading={loading} />
            </ChartCard>
          </div>

          <SectionHeading>Section 2 · Most Clients Served</SectionHeading>
          <div className="grid lg:grid-cols-1 gap-4 mb-6">
            <ChartCard title="Top 10 Staff · Unique Clients (A vs B)" testId="brand-util-chart-clients" tooltip={STAFF_UTILISATION_TIPS.clients}>
              <ABHorizontalBar rows={data?.clients_top10 || []} loading={loading} />
            </ChartCard>
          </div>

          <SectionHeading>Section 3 · Most Treatments Delivered</SectionHeading>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ChartCard title="Top 10 Staff · Treatments Delivered (A vs B)" testId="brand-util-chart-treatments" tooltip={STAFF_UTILISATION_TIPS.treatments}>
              <ABHorizontalBar rows={data?.treatments_top10 || []} loading={loading} />
            </ChartCard>
            <ChartCard title="Top 10 Treatments · by Staff (A, stacked)" testId="brand-util-chart-treatments-stack" tooltip={STAFF_UTILISATION_TIPS.treatments}>
              <TreatmentsStack
                rows={data?.treatments_stack || []}
                series={data?.treatments_stack_series || []}
                loading={loading}
              />
            </ChartCard>
          </div>
        </>
      )}
      </div>
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-from`} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input type="date" value={to}   onChange={(e) => onTo(e.target.value)}   className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-to`} />
    </div>
  );
}

function SectionHeading({ children }) {
  return <p className="tracking-overline text-[10px] text-muted-foreground mb-2">{children}</p>;
}

function ChartCard({ title, children, testId, height = 360, tooltip }) {
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-4">
        <p className="text-sm font-medium mb-3 pr-5">{title}</p>
        <div style={{ height }}>{children}</div>
      </CardContent>
    </Card>
  );
}

function Centered({ children }) {
  return <div className="h-full flex items-center justify-center text-xs text-muted-foreground">{children}</div>;
}

/* -------------------- Chart components -------------------- */

function StaffUtilBar({ rows, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length) return <Centered>No scheduled hours in Period A.</Centered>;
  const chartHeight = Math.max(360, rows.length * 32);
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 56, bottom: 8, left: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, (dataMax) => Math.max(100, Math.ceil(dataMax))]} tickFormatter={(v) => `${v}%`} />
        <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
        <ChartTooltip formatter={(v) => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="pct_a" name="Period A" fill={COLOR_A} radius={[0, 3, 3, 0]} barSize={14}>
          <LabelList dataKey="pct_a" position="right" fontSize={11} formatter={(v) => `${v}%`} />
        </Bar>
        <Bar dataKey="pct_b" name="Period B" fill={COLOR_B} radius={[0, 3, 3, 0]} barSize={8}>
          <LabelList dataKey="pct_b" position="right" fontSize={10} formatter={(v) => `${v}%`} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function TrendLines({ rows, series, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length || !series.length) return <Centered>No data in Period A.</Centered>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="bucket" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
        <ChartTooltip formatter={(v) => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <ReferenceLine y={100} stroke="#dc2626" strokeDasharray="4 2" />
        {series.map((s, i) => (
          <Line key={s} type="monotone" dataKey={s} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function ABHorizontalBar({ rows, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length) return <Centered>No data in Period A.</Centered>;
  const chartHeight = Math.max(360, rows.length * 44);
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 48, bottom: 8, left: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
        <ChartTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="count_a" name="Period A" fill={COLOR_A} radius={[0, 3, 3, 0]} barSize={14}>
          <LabelList dataKey="count_a" position="right" fontSize={11} />
        </Bar>
        <Bar dataKey="count_b" name="Period B" fill={COLOR_B} radius={[0, 3, 3, 0]} barSize={8}>
          <LabelList dataKey="count_b" position="right" fontSize={10} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function TreatmentsStack({ rows, series, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length || !series.length) return <Centered>No treatments in Period A.</Centered>;
  const chartHeight = Math.max(360, rows.length * 36);
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 32, bottom: 8, left: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 11 }} />
        <ChartTooltip />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {series.map((s, i) => (
          <Bar
            key={s}
            dataKey={s}
            stackId="treatments"
            name={s}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            radius={i === series.length - 1 ? [0, 3, 3, 0] : [0, 0, 0, 0]}
          >
            {i === series.length - 1 && <LabelList dataKey="total" position="right" fontSize={10} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
